import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert
} from '@mui/material';
import preInscricaoStatusService from '../../services/preInscricaoStatusService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const STATUS_OPTIONS = [
  { value: 'Pendente', label: 'Pendente' },
  { value: 'Em_Analise', label: 'Em Análise' },
  { value: 'Aprovada', label: 'Aprovada' },
  { value: 'Matriculado', label: 'Matriculado' },
  { value: 'Rejeitada', label: 'Rejeitada' },
  { value: 'Desistente', label: 'Desistente' }
];

function GerenciadorStatus({ preInscricaoId, statusAtual, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [novoStatus, setNovoStatus] = useState('');
  const [observacao, setObservacao] = useState('');
  const [historico, setHistorico] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const carregarHistorico = useCallback(async () => {
    try {
      const response = await preInscricaoStatusService.obterHistorico(preInscricaoId);
      setHistorico(response.data);
    } catch (err) {
      setError('Erro ao carregar histórico de status');
      console.error('Erro ao carregar histórico:', err);
    }
  }, [preInscricaoId]);

  useEffect(() => {
    if (preInscricaoId) {
      carregarHistorico();
    }
  }, [preInscricaoId, carregarHistorico]);

  const handleClickOpen = () => {
    setOpen(true);
    setNovoStatus(statusAtual);
    setObservacao('');
    setError(null);
    setSuccess(null);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      await preInscricaoStatusService.atualizarStatus(
        preInscricaoId,
        novoStatus,
        observacao
      );

      setSuccess('Status atualizado com sucesso');
      carregarHistorico();
      if (onStatusChange) {
        onStatusChange(novoStatus);
      }
      handleClose();
    } catch (err) {
      setError('Erro ao atualizar status');
      console.error('Erro ao atualizar status:', err);
    }
  };

  return React.createElement(Box, null,
    React.createElement(Button, {
      variant: 'outlined',
      onClick: handleClickOpen,
      sx: { textTransform: 'none' }
    }, 'Gerenciar Status'),

    React.createElement(Dialog, {
      open,
      onClose: handleClose,
      maxWidth: 'md',
      fullWidth: true
    },
      React.createElement(DialogTitle, null, 'Gerenciar Status da Pré-Inscrição'),
      React.createElement(DialogContent, null,
        error && React.createElement(Alert, {
          severity: 'error',
          sx: { mb: 2 }
        }, error),
        
        success && React.createElement(Alert, {
          severity: 'success',
          sx: { mb: 2 }
        }, success),

        React.createElement(Box, { sx: { mb: 3, mt: 2 } },
          React.createElement(FormControl, { fullWidth: true, sx: { mb: 2 } },
            React.createElement(InputLabel, null, 'Novo Status'),
            React.createElement(Select, {
              value: novoStatus,
              label: 'Novo Status',
              onChange: (e) => setNovoStatus(e.target.value)
            },
              STATUS_OPTIONS.map(option =>
                React.createElement(MenuItem, {
                  key: option.value,
                  value: option.value
                }, option.label)
              )
            )
          ),

          React.createElement(TextField, {
            fullWidth: true,
            label: 'Observação',
            multiline: true,
            rows: 3,
            value: observacao,
            onChange: (e) => setObservacao(e.target.value)
          })
        ),

        React.createElement(Typography, {
          variant: 'h6',
          sx: { mb: 2 }
        }, 'Histórico de Status'),

        React.createElement(Paper, {
          variant: 'outlined',
          sx: { maxHeight: 300, overflow: 'auto' }
        },
          React.createElement(List, null,
            historico.length > 0 ? historico.map((item, index) =>
              React.createElement(React.Fragment, { key: item.id },
                React.createElement(ListItem, null,
                  React.createElement(ListItemText, {
                    primary: React.createElement(Typography, { variant: 'subtitle1' },
                      `De: ${item.statusAnterior} → Para: ${item.statusNovo}`
                    ),
                    secondary: React.createElement(React.Fragment, null,
                      React.createElement(Typography, {
                        variant: 'body2',
                        color: 'text.secondary'
                      }, format(new Date(item.createdAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })),
                      item.observacao && React.createElement(Typography, {
                        variant: 'body2',
                        sx: { mt: 1 }
                      }, `Observação: ${item.observacao}`)
                    )
                  })
                ),
                index < historico.length - 1 && React.createElement(Divider)
              )
            ) : React.createElement(ListItem, null,
              React.createElement(ListItemText, {
                primary: 'Nenhuma alteração de status registrada'
              })
            )
          )
        )
      ),
      React.createElement(DialogActions, null,
        React.createElement(Button, {
          onClick: handleClose
        }, 'Cancelar'),
        React.createElement(Button, {
          onClick: handleSubmit,
          variant: 'contained',
          disabled: !novoStatus || novoStatus === statusAtual
        }, 'Atualizar Status')
      )
    )
  );
}

// Adicionando PropTypes
GerenciadorStatus.propTypes = {
  preInscricaoId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  statusAtual: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func.isRequired
};

export default GerenciadorStatus;